/**
 * TRACI-431: the embed side of "the shopper moved to a different page without a reload".
 *
 * The loader posts fresh page context, unasked, with `reason: 'navigation'` when the host
 * page's URL changes and the page stays (history.pushState / replaceState, back and
 * forward, a back/forward-cache restore). The embed stores page context once per load
 * (TRAC-694), so on its own it would drop that push as a late duplicate. This bridge:
 *
 *  - drops the page the shopper left (its working context, page data and page context),
 *    so nothing from it lingers if the new page has no tire on it, and
 *  - fires `traci:context-refresh`, the embed's existing "forget the stored page and ask
 *    the host again" path (the one Clear conversation uses), so the new page is stored
 *    by the same code that stores it on load.
 *
 * It also records the host page URL, and the page record built from the whole message
 * (TRAC-1212), from every page-context message. Cross-origin, the
 * embed cannot read the host URL itself, and a page with no tire context stores no page
 * context, so this is how the chat knows which page a message was sent from.
 *
 * A repost of the same page with the same data (the loader posts once more a beat later,
 * in case the site filled its data after moving the URL) is ignored, so a move refreshes
 * the chat once unless the page's data actually changed.
 */
export interface PageNavigationTarget {
  setParentPageUrl(url: string | null): void;
  /** TRAC-1212: the host's whole answer, for the page record the open waits on. */
  recordHostPage(data: Record<string, unknown>): void;
  setPageContext(ctx: Record<string, never>): void;
  setWorkingContext(ctx: null): void;
  setPageData(data: null): void;
}

type ContextMessage = { type?: unknown; source?: unknown; url?: unknown; reason?: unknown; timestamp?: unknown; [k: string]: unknown };

function isHostContext(data: unknown): data is ContextMessage {
  const d = data as ContextMessage | null;
  return !!d && typeof d === 'object' && d.type === 'TRACI_PAGE_CONTEXT' && d.source === 'TR-Parent';
}

/** The page as the push describes it, minus fields that change on every post. */
function signature(d: ContextMessage): string {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { timestamp, reason, openMode, html, ...rest } = d;
  try {
    return JSON.stringify(rest);
  } catch {
    return String(d.url);
  }
}

/**
 * `env` is the embed's VITE_ENV: in prod only a tirerack.com host is listened to, the
 * same rule EmbedPage applies to page context. Listen before EmbedPage does (the chat
 * service installs this at import, EmbedPage in an effect): a push then re-arms the
 * once-per-load guard just before EmbedPage stores it. In the other order the push is
 * dropped as a duplicate and the refresh's re-ask stores the new page instead.
 */
export function installPageNavigationBridge(win: Window, target: PageNavigationTarget, env = 'dev'): () => void {
  let lastPush: string | null = null;
  const onMessage = (event: MessageEvent) => {
    if (event.source !== win.parent || !isHostContext(event.data)) return;
    if (env === 'prod' && !event.origin.endsWith('.tirerack.com')) return;
    const data = event.data;
    if (typeof data.url === 'string' && data.url) target.setParentPageUrl(data.url.slice(0, 2048));
    // TRAC-1212: this listener hears the host first, ahead of EmbedPage (which also holds
    // the answer back until the content settings load). Record the page here, in full, so
    // an open waiting on it names the kind and product, not just the address.
    target.recordHostPage(data);
    if (data.reason !== 'navigation') return;
    const sig = signature(data);
    if (sig === lastPush) return;
    lastPush = sig;
    target.setWorkingContext(null);
    target.setPageData(null);
    target.setPageContext({});
    win.dispatchEvent(new CustomEvent('traci:context-refresh'));
  };
  win.addEventListener('message', onMessage);
  return () => win.removeEventListener('message', onMessage);
}
